import { getGridRowHeights } from '@/catalog-apps/components/component-library/utils/getGridRowHeights'
import { getPageHeights } from '@/catalog-apps/components/component-library/utils/getPageHeights'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import clsx from 'clsx'
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { GRID_ROW_HEIGHT_NO_HEADER, MAIN_HEADER_HEIGHT, PADDING_GAP, SIDEBAR_WIDTH } from '../../../config'
import { TContainer } from '../../types'
import { useAppPageLayout } from '../../useAppPageLayout'
import { useAppPageState } from '../../useAppPageState'
import { useGetAppPage } from '../../useGetAppPage'
import { ComponentRenderer } from './ComponentRenderer'
import { GridLayoutInfoDisplay } from './GridLayoutInfoDisplay'
import { Tile } from './Tile'
import { TileGridBuilderRGL } from './TileGridBuilderRGL'

export function TileGrid() {
  const { pageId } = useAppContext()
  const { isConfiguringLayout } = useAppPageState()
  const { data } = useGetAppPage()
  const { pageFilters, title, emphasizeHeader } = data
  const { containers, rows, cols, setDefaultContainers, gridCols, gridRows, trimGrid } = useAppPageLayout()
  const hasHeader = title || pageFilters.length
  const { headerHeight } = getPageHeights(emphasizeHeader ?? false)
  const { gridHeight } = getGridRowHeights(emphasizeHeader ?? false)

  useLayoutEffect(() => {
    setDefaultContainers()
  }, [setDefaultContainers])

  // When exiting builder, trim trailing empty rows/columns
  useEffect(() => {
    if (!isConfiguringLayout) {
      trimGrid()
    }
  }, [isConfiguringLayout, trimGrid])

  const gridContainerStyles = useMemo(
    () => ({
      gridTemplateRows: `repeat(${rows}, ${hasHeader ? gridHeight : GRID_ROW_HEIGHT_NO_HEADER})`,
      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    }),
    [cols, rows, hasHeader, gridHeight]
  )

  // placeholders used only in legacy button-based builder; not needed for RGL path

  if (isConfiguringLayout) {
    return (
      <div className={clsx('w-full overflow-visible bg-offwhite', { 'bg-secondary/20': isConfiguringLayout })}>
        <TileGridBuilderRGL />
        <div
          style={{ top: MAIN_HEADER_HEIGHT + headerHeight - PADDING_GAP, left: SIDEBAR_WIDTH - PADDING_GAP }}
          // className="fixed z-20 w-max overflow-hidden rounded bg-background shadow-lg"
          className="fixed z-20 w-max overflow-hidden bg-background shadow-lg"
        >
          <GridLayoutInfoDisplay cols={gridCols} rows={gridRows} />
        </div>
      </div>
    )
  }

  return (
    <div style={gridContainerStyles} className={clsx('grid w-full gap-5 overflow-visible bg-offwhite py-5')}>
      {containers.map((container) => {
        const { id: containerId } = container
        return <TileWithRef key={`${pageId}-${containerId}`} container={container} />
      })}
    </div>
  )
}

function TileWithRef({ container }: { container: TContainer }) {
  const tileContainerRef = useRef<HTMLDivElement>(null)
  const { component, id: containerId } = container

  return (
    <Tile ref={tileContainerRef} container={container} invisible={component.type === 'quick-nav'}>
      <ComponentRenderer component={component} containerId={containerId} tileContainerRef={tileContainerRef} />
    </Tile>
  )
}
